import { DeleteOutlined, PlusOutlined } from "@ant-design/icons"
import { Button, Form, Space } from "antd"
import { useEffect } from "react"
import { Commons, Const } from "./constants";
import { InputText, SelectWithSearch, trans } from "@comps/components"
import { makeFieldLabel } from "@utils/fieldTooltips";
import { getSlabTooltips } from "../policyManagement/common/policyTooltips";
const langKey = Const["policySlab"].lngKey
const ViewPolicySlabList = (props) => {
  const form = Form.useFormInstance();
  const slabLabel = makeFieldLabel(getSlabTooltips(props.policyType), {
    namespace: `policySlab.${props.policyType || 'default'}`,
    label: (key) => trans(`${langKey}.${key}`),
    placement: "top",
    maxWidth: 420,
  });

  useEffect(() => {

    // eslint-disable-next-line
  }, []);

  const getPrimaryTypeField = () => (
    props.policyType === 'overtime' || props.policyType === 'cpl'
      ? 'encashment_type_id'
      : 'deduction_type_id'
  );

  const getPrimaryTypes = () => (
    props.policyType === 'overtime' || props.policyType === 'cpl'
      ? props.allData?.encashment_types || []
      : props.allData?.deduction_types || []
  );

  const isFromSalary = (typeId) => {
    const selectedType = getPrimaryTypes().find((item) => `${item.id}` === `${typeId}`);
    return Boolean(selectedType?.from_salary);
  };

  const getFallbackOptions = (config) => {
    if (props.allData) {
      return getPrimaryTypes()
        .filter((item) => item.from_salary)
        .map((item) => ({
          value: item.id,
          label: item.name || item.title || item.label || item.code,
        }));
    }

    return config.options || [];
  };

  const requiredFields = [
    'start_mins',
    'min_times',
    'repeat_id',
    'deduction_type_id',
    'fallback_type_id',
    'min',
    'encashment_type_id',
  ];

  const getRules = (config) => {
    if (props.disabled || props.type === 'planner') {
      return [];
    }

    if (config.rules?.length) {
      return config.rules;
    }

    return requiredFields.includes(config.name)
      ? [{ required: true, message: trans(`${langKey}.${config.name}.req`) }]
      : [];
  };

  const getPlaceholder = (config) => {
    const placeholders = {
      start_mins: "e.g. 15 minutes",
      end_mins: "Leave blank for no upper limit",
      min_times: "e.g. 1st time",
      max_times: "Leave blank for no upper limit",
      repeat_id: "Select when deduction repeats",
      deduction_type_id: "Select salary or quota deduction",
      fallback_type_id: "Select salary fallback when quota ends",
      encashment_type_id: "Select overtime earning rule",
      min: "e.g. 60 approved minutes",
      max: "Leave blank for no upper limit",
    };

    return placeholders[config.name] || trans(`${langKey}.${config.name}`);
  };

  const getColumnStyle = (config) => {
    const compactNumberFields = ['start_mins', 'end_mins', 'min_times', 'max_times', 'min', 'max'];
    const width = compactNumberFields.includes(config.name) ? 112 : 156;

    return {
      width,
      minWidth: width,
      maxWidth: width,
      paddingLeft: 8,
      paddingRight: 8,
    };
  };

  const renderField = (config, name, restField) => {
    if (config.name === 'fallback_type_id') {
      return (
        <Form.Item
          noStyle
          shouldUpdate={ (prevValues, currentValues) => (
            prevValues?.slabs?.[name]?.[getPrimaryTypeField()] !== currentValues?.slabs?.[name]?.[getPrimaryTypeField()]
          ) }
        >
          { ({ getFieldValue }) => {
            const selectedTypeId = getFieldValue(['slabs', name, getPrimaryTypeField()]);
            const shouldShowFallback = !isFromSalary(selectedTypeId);

            if (!shouldShowFallback) {
              return null;
            }

            return (
              <Form.Item
                { ...restField }
                name={ [name, config.name] }
                rules={ getRules(config) }
              >
                <SelectWithSearch options={ getFallbackOptions(config) } placeholder={ getPlaceholder(config) } disabled={ props.disabled || props.type === 'planner' } />
              </Form.Item>
            );
          } }
        </Form.Item>
      );
    }

    return (
      <Form.Item
        { ...restField }
        name={ [name, config.name] }
        rules={ getRules(config) }
      >
        {
          config.type === 'text' || config.type === 'number' ?
            <InputText type={ config.type } placeholder={ getPlaceholder(config) } disabled={ props.disabled || props.type === 'planner' } />
            : (
              <SelectWithSearch
                options={ config.options }
                placeholder={ getPlaceholder(config) }
                disabled={ props.disabled || props.type === 'planner' }
                onChange={ () => {
                  if (config.name === getPrimaryTypeField()) {
                    form?.setFieldValue(['slabs', name, 'fallback_type_id'], null);
                  }
                } }
              />
            )

        }
      </Form.Item>
    );
  };


  return (
    <Form.List
      name="slabs"
      rules={ [
        {
          validator: async (_, slabs) => {
            if (props.disabled || props.type === 'planner' || (slabs && slabs.length > 0)) {
              return;
            }
            throw new Error("Add at least one slab before saving this policy.");
          },
        },
      ] }
    >
      { (fields, { add, remove }, { errors }) => (
        <>
          { !props.disabled && props.type !== 'planner' ? (
            <Button onClick={ () => {
              add({})
            } } icon={ <PlusOutlined /> } type="primary" className="add-node-button">{ trans(`${langKey}.add`) } </Button>
          ) : null }
          <div className="table-container">
            <table className="table" style={ { tableLayout: "fixed", width: "100%" } }>
              <thead>
                <tr>

                  {
                    props.fields.map(config => (
                      <th key={ config.name } style={ getColumnStyle(config) }>{ slabLabel(config.name) }</th>
                    ))
                  }
                  { props.type !== 'planner' && <th style={ { width: 54, minWidth: 54, paddingLeft: 8, paddingRight: 8 } }>{ trans(Commons.actions) }</th> }

                </tr>
              </thead>
              <tbody>
                { fields.map(({ key, name, ...restField }) => (
                    <tr key={ key }>
                      {
                        props.fields.map(config => (
                          <td key={ config.name } style={ getColumnStyle(config) }>
                            { renderField(config, name, restField) }
                          </td>
                        ))
                      }

                      {
                        props.type !== 'planner' &&
                        <td style={ { width: 54, minWidth: 54, paddingLeft: 8, paddingRight: 8 } }>
                          <Space className="formlist-remove">
                            <DeleteOutlined onClick={ () => {
                              remove(name)
                              props.setRows?.(key, 'Deleted')
                            } } />
                          </Space>
                        </td>
                      }
                    </tr>
                )) }
              </tbody>
            </table>
            <Form.ErrorList errors={ errors } />
          </div>
        </>
      ) }
    </Form.List>
  )
}
export default ViewPolicySlabList

//required props
//!slab.deduction_type.from_salary (fallback type)
